import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'

import { ROLES } from 'constants/security'
import { ChildBooking } from 'services/booking/childBooking/constants'
import { Child } from 'services/child/constants'
import { Nursery } from 'services/nurseries/models'

import auth from 'utils/auth'
import { generateRoute } from 'utils/routing'

import appSelectors from 'services/app/selectors'
import { withChildBookingService, withChildBookingServiceProps } from 'services/booking/childBooking'
import { withChildSessionsService, withChildSessionsServiceProps } from 'services/legacy/childSessions'
import * as childSessionsSelectors from 'services/legacy/childSessions/selectors'
import { withNurseriesService, withNurseriesServiceProps } from 'services/nurseries'
import { withRegistrationFormsService, withRegistrationFormsServiceProps } from 'services/registrationForms'
import { withRouter, withRouterProps } from 'services/router'
import { withModalService, withModalServiceProps } from 'services/utils/modal'
import { withSnackbarService, withSnackbarServiceProps } from 'services/utils/snackbar'

import { CHILD_EDIT_FORM_ROUTE_TYPE } from 'module/Children/Child/constants'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import { SendRegistrationFormModalFormValues } from './components/SendRegistrationFormModalForm'
import SendRegistrationFormModalView from './SendRegistrationFormModalView'

const CHILD_BOOKING_GROUPS = {
  read: [
    'child',
    'period',
    'childBooking',
    'childBooking.attendancePeriod',
    'childBooking.child',
    'childBooking.childProducts',
    'childBookingPreview.product',
    'regularChildProduct',
    'regularChildProduct.product',
    'nurserySessionProduct',
    'nurserySessionProduct.items',
    'nurseryDiscountProduct',
    'nurseryItemProduct',
    'nurseryFundingProduct',
    'oneOffChildProduct.product',
    'regularChildProduct.settings',
    'regularChildProductSettings',
  ],
}

const NURSERY_FORM_BUILDER_GROUPS = {
  read: [
    'nursery.settings',
    'nurserySettings',
    'nurserySettings.formBuilder',
    'formBuilderSettings',
  ],
}

const mapState = (state: RootState, { params }) => ({
  childSessions: childSessionsSelectors.getChildSessionsListSelectors(state),
  hasAccessToEditRegularBooking: auth.SELECTORS.getIsAuthorised(state, {
    roles: [
      ROLES.ORGANIZATION_DIRECTOR,
      ROLES.ORGANIZATION_NATIONAL_ADMIN,
      ROLES.ORGANIZATION_FINANCE_ADMIN,
      ROLES.ORGANIZATION_LINE_MANAGER,
      ROLES.DEPUTY_MANAGER,
      ROLES.NURSERY_MANAGER,
      ROLES.NURSERY_ADMIN,
      ROLES.SUPER_ADMIN,
    ],
  }),
  isFinanceV3Enabled: auth.SELECTORS.getIsFinanceV3Enabled(state),
  nurseryOptions: appSelectors.getContextNurseryRouterConfig(state, params),
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

export interface SendRegistrationFormModalProps {
  child: Child
  onSubmitSuccess: () => void
}

export type SendRegistrationFormModalContainerProps = SendRegistrationFormModalProps
  & withModalServiceProps
  & withChildBookingServiceProps
  & withChildSessionsServiceProps
  & withRegistrationFormsServiceProps
  & withNurseriesServiceProps
  & withSnackbarServiceProps
  & withRouterProps
  & PropsFromRedux

const SendRegistrationFormModalContainer: React.FC<SendRegistrationFormModalContainerProps> = ({
  child,
  childBookingActions,
  childBookingSelectors,
  childSessions,
  childSessionsActions,
  hasAccessToEditRegularBooking,
  isFinanceV3Enabled,
  modalActions,
  nurseriesActions,
  nurseryOptions,
  onSubmitSuccess,
  registrationFormsActions,
  registrationFormsSelectors,
  router,
  snackbarActions,
}) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isChildBookingFetching, setIsChildBookingFetching] = useState<boolean>(true)
  const [isNurserySettingsFetching, setIsNurserySettingsFetching] = useState<boolean>(true)
  const [childBookings, setChildBookings] = useState<ChildBooking[]>()
  const [nursery, setNursery] = useState<Nursery>()

  const fetch = () => {
    setIsChildBookingFetching(true)
    setIsNurserySettingsFetching(true)

    if (isFinanceV3Enabled) {
      const criteria = childBookingSelectors.getCriteria({
        childId: child?.id as number,
      })

      const params = {
        criteria,
        groups: CHILD_BOOKING_GROUPS,
        order: {
          sortField: 'startDate',
          sortOrder: 'DESC',
        },
        page: 1,
      }

      const deviceTokenNotRequired = false

      childBookingActions.list({
        onFailed: () => { setIsChildBookingFetching(false) },
        onSuccess: ({ data }) => {
          setChildBookings(data)
          setIsChildBookingFetching(false)
        },
        onlyData: true,
        params: [deviceTokenNotRequired, params],
      })
    } else {
      const criteria = childSessionsSelectors.getCriteria({ archived: 0 })

      childSessionsActions.groupedByList(child.id, {
        criteria,
        includes: ['attendancePeriod', 'invoiced', 'fundingAssigned'],
      }, () => {
        setIsChildBookingFetching(false)
      })
    }

    nurseriesActions.get(nurseryOptions.id, {
      onFailed: () => {
        setIsNurserySettingsFetching(false)
      },
      onSuccess: ({ data }) => {
        setIsNurserySettingsFetching(false)
        setNursery(data)
      },
      onlyData: true,
      params: { groups: NURSERY_FORM_BUILDER_GROUPS },
    })
  }

  useEffect(() => {
    fetch()
  }, [])

  const handleCancelClick = () => {
    modalActions.hide()
  }

  const handleSubmit = (formValues: SendRegistrationFormModalFormValues) => {
    setIsSubmitting(true)

    const body = registrationFormsSelectors.getSendRegistrationFormBody({
      childId: child.id as number,
      recipientId: formValues.recipient.value as number,
    })

    registrationFormsActions.send({
      body,
      onSuccess: () => {
        snackbarActions.show({
          message: i18n.t('modals:SendRegistrationForm:Form:sendSuccessMessage', {
            name: formValues?.recipient?.label || '',
          }),
        })
        setIsSubmitting(false)
        modalActions.hide()
        onSubmitSuccess()
      },
    })
  }

  const handleEditChildBookingClick = (childBooking: ChildBooking) => {
    handleCancelClick()

    router.push(generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.EDIT', {
      bookingId: childBooking.id,
      childId: child.id,
    }))
  }

  const handleEnrolmentDetailEditClick = () => {
    handleCancelClick()

    router.push(generateRoute('CHILDREN.EDIT', {
      childId: child.id,
      type: CHILD_EDIT_FORM_ROUTE_TYPE.ENROLMENT_DETAILS,
    }))
  }

  const handleCreateRegularBookingClick = () => {
    handleCancelClick()

    const sessionRoute = isFinanceV3Enabled
      ? generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.REGULAR_BOOKINGS.ADD', { childId: child.id })
      : generateRoute('CHILDREN.CHILD.BOOKING_PATTERN.SESSIONS', { childId: child.id })

    router.push(sessionRoute)
  }

  return (
    <SendRegistrationFormModalView
      child={child}
      childBookings={childBookings}
      childSessions={childSessions}
      hasAccessToEditRegularBooking={hasAccessToEditRegularBooking}
      isFinanceV3Enabled={isFinanceV3Enabled}
      isLoading={isChildBookingFetching || isNurserySettingsFetching}
      isSubmitting={isSubmitting}
      nursery={nursery}
      onCancelClick={handleCancelClick}
      onCreateRegularBookingClick={handleCreateRegularBookingClick}
      onEditChildBookingClick={handleEditChildBookingClick}
      onEnrolmentDetailEditClick={handleEnrolmentDetailEditClick}
      onSubmit={handleSubmit}
    />
  )
}

const enhance = compose(
  withModalService,
  withChildBookingService,
  withChildSessionsService,
  withRegistrationFormsService,
  withNurseriesService,
  withSnackbarService,
  withRouter,
  connector,
)

export default enhance(SendRegistrationFormModalContainer)
